import {componentLoader, Loadable} from "@bounce/shared/helpers";
import {lazy} from "react";
import {RouteObject} from "react-router-dom";
import AccountLayout from "~/layout/account/AccountLayout";
import {BlockedAccountGuard} from "~/new-design/guards/BlockedAccountGuard";
import {QuestionnaireVariantGate} from "~/new-design/guards/QuestionnaireVariantGate";
import {NewDesignGate} from "~/new-design/guards/NewDesignGate";
import {NewDesignOnlyGate} from "~/new-design/guards/NewDesignOnlyGate";
import {consumerUrls as urls} from "~/routes/consumerUrls";
import {AgentConsentGuard} from "~/pages/agent-consent/AgentConsentGuard";
import AuthGuard from "./guards/AuthGuard";
import AuthRoleGuard from "./guards/AuthRoleGuard";
import GuestGuard from "./guards/GuestGuard";
import UiGuard from "./guards/UiGuard";

const ErrorPage = Loadable(lazy(() => componentLoader(() => import("~/pages/error/ErrorPage"))));

// New design pages
const WelcomePage = Loadable(lazy(() => componentLoader(() => import("~/new-design/pages/welcome/WelcomePage"))));
const PickerPage = Loadable(lazy(() => componentLoader(() => import("~/new-design/pages/picker/PickerPage"))));
const EducationalFlowPage = Loadable(
  lazy(() => componentLoader(() => import("~/new-design/pages/educational/EducationalFlowPage")))
);
const QuestionnaireIntroPage = Loadable(
  lazy(() => componentLoader(() => import("~/new-design/pages/questionnaire/QuestionnaireIntroPage")))
);
const QuestionnairePage = Loadable(
  lazy(() => componentLoader(() => import("~/new-design/pages/questionnaire/QuestionnairePage")))
);
const QuestionnaireLoaderPage = Loadable(
  lazy(() => componentLoader(() => import("~/new-design/pages/questionnaire/QuestionnaireLoaderPage")))
);
const ReviewPage = Loadable(lazy(() => componentLoader(() => import("~/new-design/pages/review/ReviewPage"))));
const CheckoutPage = Loadable(lazy(() => componentLoader(() => import("~/new-design/pages/checkout/CheckoutPage"))));
const PaymentSuccessPage = Loadable(
  lazy(() => componentLoader(() => import("~/new-design/pages/payment-success/PaymentSuccessPage")))
);
const NewDesignHardshipIntro = Loadable(
  lazy(() => componentLoader(() => import("~/new-design/pages/hardship/HardshipIntro")))
);
const NewDesignHardshipPlans = Loadable(
  lazy(() => componentLoader(() => import("~/new-design/pages/hardship/HardshipPlans")))
);
const HardshipPicker = Loadable(lazy(() => componentLoader(() => import("~/new-design/pages/picker/HardshipPicker"))));
const ProfilePage = Loadable(lazy(() => componentLoader(() => import("~/new-design/pages/profile/ProfilePage"))));

const AgentConsent = Loadable(lazy(() => componentLoader(() => import("~/pages/agent-consent/AgentConsent"))));

// Auth pages
const Login = Loadable(lazy(() => componentLoader(() => import("~/new-design/pages/login/LoginWrapper"))));
const VerifyCode = Loadable(lazy(() => componentLoader(() => import("~/new-design/pages/login/VerifyCode"))));
const VerifyAccount = Loadable(lazy(() => componentLoader(() => import("~/new-design/pages/login/VerifyAccount"))));
const VerifyAnonymousAccount = Loadable(
  lazy(() => componentLoader(() => import("~/new-design/pages/login/VerifyAnonymousAccount")))
);

// Legacy auth pages (white label)
const LegacyLogin = Loadable(lazy(() => componentLoader(() => import("~/pages/auth/login/LegacyLogin"))));
const LegacyVerifyCode = Loadable(lazy(() => componentLoader(() => import("~/pages/auth/login/VerifyCode"))));
const LegacyVerifyAccount = Loadable(lazy(() => componentLoader(() => import("~/pages/auth/login/VerifyAccount"))));
const LegacyVerifyAnonymousAccount = Loadable(
  lazy(() => componentLoader(() => import("~/pages/auth/login/VerifyAnonymousAccount")))
);

// Legacy account pages
const Account = Loadable(lazy(() => componentLoader(() => import("~/pages/account/account/Account"))));
const Accounts = Loadable(lazy(() => componentLoader(() => import("~/pages/account/accounts/Accounts"))));
const AccountDashboardPage = Loadable(
  lazy(() => componentLoader(() => import("~/pages/account/account-dashboard/AccountDashboardPage")))
);
const DashboardPage = Loadable(lazy(() => componentLoader(() => import("~/new-design/pages/dashboard/DashboardPage"))));
const RetryPaymentPage = Loadable(
  lazy(() => componentLoader(() => import("~/new-design/pages/retry-payment/RetryPaymentPage")))
);
const DisputePage = Loadable(lazy(() => componentLoader(() => import("~/new-design/pages/dispute/DisputePage"))));
const VerificationRequestPage = Loadable(
  lazy(() => componentLoader(() => import("~/new-design/pages/verification-request/VerificationRequestPage")))
);
const DisputeSuccess = Loadable(
  lazy(() => componentLoader(() => import("~/new-design/pages/dispute/DisputeSuccessPage")))
);
const PresetPlanCheckoutValidator = Loadable(
  lazy(() => componentLoader(() => import("~/pages/account/payment/PresetPlanCheckoutValidator")))
);
const GuestPaymentCheckout = Loadable(
  lazy(() => componentLoader(() => import("~/pages/account/payment/guest-payment/GuestPaymentCheckout")))
);
const PlanOfferReviewPage = Loadable(
  lazy(() => componentLoader(() => import("~/new-design/pages/plan-offer-review/PlanOfferReviewPage")))
);
const GuestPaymentPage = Loadable(
  lazy(() => componentLoader(() => import("~/new-design/pages/guest-payment/GuestPaymentPage")))
);
const GuestPaymentPlanCheckout = Loadable(
  lazy(() => componentLoader(() => import("~/pages/account/payment/guest-payment/GuestPaymentPlanCheckout")))
);
const GuestCustomPaymentPlanCheckout = Loadable(
  lazy(() => componentLoader(() => import("~/pages/account/payment/guest-payment/GuestCustomPaymentPlanCheckout")))
);
const GuestMatrixPaymentPlanCheckout = Loadable(
  lazy(() => componentLoader(() => import("~/pages/account/payment/guest-payment/GuestMatrixPaymentPlanCheckout")))
);
const PaymentSuccess = Loadable(lazy(() => componentLoader(() => import("~/pages/account/success/PaymentSuccess"))));
const ManagePaymentMethod = Loadable(
  lazy(() => componentLoader(() => import("~/pages/account/manage-payment-method/ManagePaymentMethod")))
);
const CustomPayment = Loadable(
  lazy(() => componentLoader(() => import("~/pages/account/payment-plans/PartialPayment")))
);
const PartialPaymentPage = Loadable(
  lazy(() => componentLoader(() => import("~/new-design/pages/partial-payment/PartialPaymentPage")))
);
const HardshipIntro = Loadable(
  lazy(() => componentLoader(() => import("~/pages/account/payment-plans/hardship-plan/HardshipIntro")))
);
const HardshipPlans = Loadable(
  lazy(() => componentLoader(() => import("~/pages/account/payment-plans/hardship-plan/HardshipPlans")))
);
const ExpiredCampaign = Loadable(
  lazy(() => componentLoader(() => import("~/pages/account/expired-campaign/ExpiredCampaign")))
);
const SummaryPage = Loadable(lazy(() => componentLoader(() => import("~/pages/account/payment-plans/Summary"))));
const SummaryPageValidator = Loadable(
  lazy(() => componentLoader(() => import("~/pages/account/payment-plans/SummaryPageValidator")))
);

export const consumerRoutes: RouteObject[] = [
  // Multi Accounts
  {
    path: urls.accounts.path,
    element: (
      <AuthGuard>
        <UiGuard>
          <AccountLayout />
        </UiGuard>
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: (
          <AuthRoleGuard>
            <Accounts />
          </AuthRoleGuard>
        ),
      },
    ],
  },
  // Profile
  {
    path: urls.profile.path,
    element: (
      <AuthGuard>
        <UiGuard>
          <AccountLayout />
        </UiGuard>
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: (
          <AuthRoleGuard allowNoLogin>
            <ProfilePage />
          </AuthRoleGuard>
        ),
      },
    ],
  },
  // Auth Routes
  {
    path: urls.auth.path,
    element: (
      <GuestGuard>
        <UiGuard>
          <AccountLayout />
        </UiGuard>
      </GuestGuard>
    ),
    children: [
      {path: urls.login.path, element: <NewDesignGate newDesign={<Login />} legacy={<LegacyLogin />} />},
      {path: urls.deprecatedLogin.path, element: <NewDesignGate newDesign={<Login />} legacy={<LegacyLogin />} />},
      {
        path: urls.verifyAccount.path,
        element: <NewDesignGate newDesign={<VerifyAccount />} legacy={<LegacyVerifyAccount />} />,
      },
      {
        path: urls.verifyCodeFromUrl.path,
        element: <NewDesignGate newDesign={<VerifyCode />} legacy={<LegacyVerifyCode />} />,
      },
      {
        path: urls.verifyCodeFromUrlWithCampaign.path,
        element: <NewDesignGate newDesign={<VerifyCode />} legacy={<LegacyVerifyCode />} />,
      },
      {
        path: urls.verifyAnonymousAccount.path,
        element: <NewDesignGate newDesign={<VerifyAnonymousAccount />} legacy={<LegacyVerifyAnonymousAccount />} />,
      },
      {path: urls.verifyCode.path, element: <NewDesignGate newDesign={<VerifyCode />} legacy={<LegacyVerifyCode />} />},
      {path: "", element: <ErrorPage />},
      {path: "*", element: <ErrorPage />},
    ],
  },
  // Account Routes
  {
    path: urls.account.path,
    element: (
      <AuthGuard>
        <UiGuard>
          <AccountLayout />
        </UiGuard>
      </AuthGuard>
    ),
    children: [
      {
        path: "",
        element: (
          <AuthRoleGuard>
            <Accounts />
          </AuthRoleGuard>
        ),
      },
      {
        path: urls.accountMain.path,
        element: (
          <AuthRoleGuard allowNoLogin>
            <NewDesignGate
              newDesign={
                <BlockedAccountGuard>
                  <WelcomePage />
                </BlockedAccountGuard>
              }
              legacy={<Account />}
            />
          </AuthRoleGuard>
        ),
      },
      // New-design-only routes
      {
        path: urls.picker.path,
        element: (
          <AuthRoleGuard allowNoLogin>
            <NewDesignOnlyGate>
              <BlockedAccountGuard>
                <PickerPage />
              </BlockedAccountGuard>
            </NewDesignOnlyGate>
          </AuthRoleGuard>
        ),
      },
      {
        path: urls.educational.path,
        element: (
          <AuthRoleGuard allowNoLogin>
            <NewDesignOnlyGate>
              <BlockedAccountGuard>
                <QuestionnaireVariantGate>
                  <EducationalFlowPage />
                </QuestionnaireVariantGate>
              </BlockedAccountGuard>
            </NewDesignOnlyGate>
          </AuthRoleGuard>
        ),
      },
      {
        path: urls.questionnaireIntro.path,
        element: (
          <AuthRoleGuard allowNoLogin>
            <NewDesignOnlyGate>
              <BlockedAccountGuard>
                <QuestionnaireVariantGate>
                  <QuestionnaireIntroPage />
                </QuestionnaireVariantGate>
              </BlockedAccountGuard>
            </NewDesignOnlyGate>
          </AuthRoleGuard>
        ),
      },
      {
        path: urls.questionnaire.path,
        element: (
          <AuthRoleGuard allowNoLogin>
            <NewDesignOnlyGate>
              <BlockedAccountGuard>
                <QuestionnaireVariantGate>
                  <QuestionnairePage />
                </QuestionnaireVariantGate>
              </BlockedAccountGuard>
            </NewDesignOnlyGate>
          </AuthRoleGuard>
        ),
      },
      {
        path: urls.questionnaireLoader.path,
        element: (
          <AuthRoleGuard allowNoLogin>
            <NewDesignOnlyGate>
              <BlockedAccountGuard>
                <QuestionnaireVariantGate>
                  <QuestionnaireLoaderPage />
                </QuestionnaireVariantGate>
              </BlockedAccountGuard>
            </NewDesignOnlyGate>
          </AuthRoleGuard>
        ),
      },
      {
        path: urls.questionnaireHardshipPlans.path,
        element: (
          <AuthRoleGuard allowNoLogin>
            <NewDesignOnlyGate>
              <BlockedAccountGuard>
                <HardshipPicker />
              </BlockedAccountGuard>
            </NewDesignOnlyGate>
          </AuthRoleGuard>
        ),
      },
      // Gated routes (new design / legacy)
      {
        path: urls.hardshipIntro.path,
        element: (
          <AuthRoleGuard allowNoLogin>
            <NewDesignGate
              newDesign={
                <BlockedAccountGuard>
                  <NewDesignHardshipIntro />
                </BlockedAccountGuard>
              }
              legacy={<HardshipIntro />}
            />
          </AuthRoleGuard>
        ),
      },
      {
        path: urls.hardshipPlans.path,
        element: (
          <AuthRoleGuard allowNoLogin>
            <NewDesignGate
              newDesign={
                <BlockedAccountGuard>
                  <NewDesignHardshipPlans />
                </BlockedAccountGuard>
              }
              legacy={<HardshipPlans />}
            />
          </AuthRoleGuard>
        ),
      },
      {
        path: urls.accountDashboard.path,
        element: (
          <AuthRoleGuard allowNoLogin>
            <NewDesignGate newDesign={<DashboardPage />} legacy={<AccountDashboardPage />} />
          </AuthRoleGuard>
        ),
      },
      {
        path: urls.retryPayment.path,
        element: (
          <AuthRoleGuard allowNoLogin>
            <RetryPaymentPage />
          </AuthRoleGuard>
        ),
      },
      // Legacy routes
      {
        path: urls.paymentSuccess.path,
        element: (
          <AuthRoleGuard allowNoLogin>
            <NewDesignGate newDesign={<PaymentSuccessPage />} legacy={<PaymentSuccess />} />
          </AuthRoleGuard>
        ),
      },
      {
        path: urls.disputeSuccess.path,
        element: <DisputeSuccess />,
      },
      {
        path: urls.dispute.path,
        element: (
          <AuthRoleGuard>
            <DisputePage />
          </AuthRoleGuard>
        ),
      },
      {
        path: urls.verificationRequest.path,
        element: (
          <AuthRoleGuard>
            <VerificationRequestPage />
          </AuthRoleGuard>
        ),
      },
      {
        path: urls.expiredCampaign.path,
        element: <ExpiredCampaign />,
      },
      {
        path: urls.managePaymentMethod.path,
        element: (
          <AuthRoleGuard>
            <ManagePaymentMethod />
          </AuthRoleGuard>
        ),
      },
      {
        path: urls.summaryPage.path,
        element: (
          <AuthRoleGuard allowNoLogin>
            <NewDesignGate
              newDesign={<ReviewPage />}
              legacy={
                <SummaryPageValidator>
                  <SummaryPage />
                </SummaryPageValidator>
              }
            />
          </AuthRoleGuard>
        ),
      },
      {
        path: urls.presetPlanCheckout.path,
        element: (
          <AuthRoleGuard allowNoLogin>
            <NewDesignGate newDesign={<CheckoutPage />} legacy={<PresetPlanCheckoutValidator />} />
          </AuthRoleGuard>
        ),
      },
      {
        path: urls.partialPayment.path,
        element: (
          <AuthRoleGuard allowNoLogin>
            <NewDesignGate newDesign={<PartialPaymentPage />} legacy={<CustomPayment />} />
          </AuthRoleGuard>
        ),
      },
    ],
  },
  // Guest payment plan checkout routes
  {
    path: urls.guestPaymentPlanRoot.path,
    element: (
      <GuestGuard unknownUser={true}>
        <UiGuard>
          <AccountLayout />
        </UiGuard>
      </GuestGuard>
    ),
    children: [
      {path: urls.guestPaymentPlanCheckout.path, element: <GuestPaymentPlanCheckout />},
      {path: urls.guestCustomPaymentPlanCheckout.path, element: <GuestCustomPaymentPlanCheckout />},
      {path: urls.guestMatrixPaymentPlanCheckout.path, element: <GuestMatrixPaymentPlanCheckout />},
      {
        path: urls.guestPaymentSuccess.path,
        element: <NewDesignGate newDesign={<PaymentSuccessPage />} legacy={<PaymentSuccess />} />,
      },
    ],
  },
  {
    path: urls.planOfferReview.path,
    element: (
      <GuestGuard unknownUser={true}>
        <UiGuard>
          <PlanOfferReviewPage />
        </UiGuard>
      </GuestGuard>
    ),
  },
  // Guest payment checkout routes
  {
    path: urls.guestPaymentRoot.path,
    element: (
      <GuestGuard unknownUser={true}>
        <UiGuard>
          <AccountLayout />
        </UiGuard>
      </GuestGuard>
    ),
    children: [
      {
        path: urls.guestPaymentCheckout.path,
        element: <NewDesignGate newDesign={<GuestPaymentPage />} legacy={<GuestPaymentCheckout />} />,
      },
      {
        path: urls.guestPaymentSuccess.path,
        element: <NewDesignGate newDesign={<PaymentSuccessPage />} legacy={<PaymentSuccess />} />,
      },
    ],
  },
  {
    path: urls.agentConsent.path,
    element: (
      <AgentConsentGuard>
        <AgentConsent />
      </AgentConsentGuard>
    ),
  },
];
